import { t } from 'i18next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  SupportChatMessage,
  SupportChatService,
} from '../../services/support-chat'

const GREETING_ID = 'greeting'

let idCounter = 0
const nextId = () => {
  idCounter += 1
  return `${Date.now()}-${idCounter}`
}

const createGreeting = (gymName?: string): SupportChatMessage => ({
  id: GREETING_ID,
  role: 'assistant',
  content: gymName
    ? t(
        "Hi! I'm the help assistant. Ask me how to book classes, buy or manage your membership, update your payment details or sign in.\n\nI can't see your account, so for anything about your own account please speak to the team at {{gymName}}.",
        { gymName }
      )
    : t(
        "Hi! I'm the help assistant. Ask me how to book classes, buy or manage your membership, update your payment details or sign in.\n\nI can't see your account, so for anything about your own account please speak to your gym's team."
      ),
  citations: [],
})

/** A stored conversation that never got past the greeting gets a fresh one. */
const initialMessages = (
  stored: SupportChatMessage[] | undefined,
  gymName?: string
) =>
  stored?.length && !(stored.length === 1 && stored[0].id === GREETING_ID)
    ? stored
    : [createGreeting(gymName)]

/**
 * State for one member's support conversation. The parent owns it, so a reply
 * that arrives while the panel is closed is not lost.
 */
const useSupportChat = ({ userId, gymName }: UseSupportChatOptions) => {
  const restored = useMemo(() => SupportChatService.restore(userId), [userId])

  const [sessionId, setSessionId] = useState(restored?.sessionId)
  const [messages, setMessages] = useState(() =>
    initialMessages(restored?.messages, gymName)
  )
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState<string>()

  const mounted = useRef(true)
  // Bumped by `reset`, so a reply to an abandoned conversation is dropped
  const conversation = useRef(0)
  useEffect(
    () => () => {
      mounted.current = false
    },
    []
  )

  useEffect(() => {
    SupportChatService.persist({ userId, sessionId, messages })
  }, [userId, sessionId, messages])

  const submit = useCallback(
    async (text: string, currentSessionId: string | undefined) => {
      const current = conversation.current
      const isStale = () => !mounted.current || conversation.current !== current
      setSending(true)
      setError(undefined)
      setPending(text)

      try {
        const reply = await SupportChatService.sendMessage({
          message: text,
          sessionId: currentSessionId,
        })
        if (isStale()) {
          return
        }

        setSessionId(reply.sessionId || currentSessionId)
        setMessages((prev) => [
          ...prev,
          {
            id: nextId(),
            role: 'assistant',
            content: reply.answer || '',
            citations: Array.isArray(reply.citations)
              ? reply.citations.filter(
                  (citation) => citation && typeof citation.url === 'string'
                )
              : [],
          },
        ])
        setPending(undefined)
      } catch (e) {
        if (isStale()) {
          return
        }
        // The session belongs to someone else or expired: retry in a new one
        if (SupportChatService.isSessionRejected(e)) {
          setSessionId(undefined)
        }
        setError(SupportChatService.getErrorMessage(e))
      } finally {
        if (!isStale()) {
          setSending(false)
        }
      }
    },
    []
  )

  const send = useCallback(
    (rawText: string) => {
      const text = rawText.trim()
      if (!text || sending) {
        return false
      }
      if (text.length > SupportChatService.MAX_MESSAGE_LENGTH) {
        setError(
          t('Messages are limited to {{max}} characters.', {
            max: SupportChatService.MAX_MESSAGE_LENGTH,
          })
        )
        return false
      }

      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'user', content: text, citations: [] },
      ])
      submit(text, sessionId)
      return true
    },
    [sending, sessionId, submit]
  )

  const retry = useCallback(() => {
    if (pending && !sending) {
      submit(pending, sessionId)
    }
  }, [pending, sending, sessionId, submit])

  const reset = useCallback(() => {
    conversation.current += 1
    SupportChatService.clear()
    setSessionId(undefined)
    setMessages([createGreeting(gymName)])
    setError(undefined)
    setPending(undefined)
    setSending(false)
  }, [gymName])

  return {
    messages,
    sending,
    error,
    canRetry: !!pending && !sending,
    send,
    retry,
    reset,
  }
}

type UseSupportChatOptions = {
  userId: number
  gymName?: string
}

type SupportChat = ReturnType<typeof useSupportChat>

export { createGreeting, useSupportChat }
export type { SupportChat }
